import { clsx } from 'clsx';
// 🚨 Important! `TableTh` shares styles with `TableTd`. While this is
// generally frowned upon, it's fairly crucial to keep the styles between
// the components in sync.
import styles from './TableTd.module.scss';

type Props = {
  /**
   * The content to render within the component.
   */
  children: React.ReactNode;
  /**
   * Styling hook.
   */
  className?: string;
  /**
   * Whether to freeze the cell during scrolling.
   */
  freeze?: 'row' | 'col' | 'row col';
  /**
   * Whether to render a shadow when the cell is frozen.
   */
  shadowed?: 'row' | 'col' | 'row col';
  /**
   * The `headers` attribute for `th`. Contains a space-separated list
   * of `id`s that reference `th` tags that describe `th`. Needed for a11y.
   */
  headers?: string;
  /**
   * The `id` attribute for `th`. Used for accessibility.
   */
  id?: string;
  /**
   * The `role` attribute for `th`, indicating which axis `th` applies to.
   */
  role?: 'columnheader' | 'rowheader';
  /**
   * The scope attribute for `th`, indicating what `th` applies to.
   */
  scope?: 'col' | 'row';
};

/**
 * An individual `th` table cell. 🚨 Note that this component shares a
 * lot of commonality with `TableTd`, however they have different purposes
 * and `Props` requirements so they're defined distinctly.
 */
export const TableTh = ({
  children,
  className,
  freeze,
  headers,
  id,
  scope,
  shadowed,
  role = 'columnheader',
}: Props) => {
  return (
    <th
      className={clsx(styles.tableTd, className)}
      data-freeze={freeze}
      data-shadowed={shadowed}
      data-zds
      headers={headers}
      id={id}
      role={role}
      scope={scope}
    >
      {children}
    </th>
  );
};
